import React, { FunctionComponent, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import PublicPage from 'core/auth/components/public-page';
import LoginMask, { LoginForm, LoginFormFields } from './login-mask';
import { useLogin, useRedirectPathSelector } from 'core/auth/hooks';
import Translate from 'ui/atoms/translate';
import NarrowContent from 'core/layout/content/narrow-content';
import BoxedContent from 'ui/molecules/boxed-content';
import useGoTo from 'hooks/use-go-to';
import ActionButtons from 'ui/molecules/action-buttons';
import Button from 'ui/atoms/button';
import { AUTH_ROUTES } from 'core/auth/pages/routes.config';
import Section from 'ui/atoms/section';
import Header from 'ui/atoms/header';
import { AppType } from 'core/auth/types';
import applicationConfig from 'core/config/local';
import { LoginTypeEnum } from 'api/models/LoginTypeEnum';
import { OrganizationsApi } from 'api/apis/OrganizationsApi';
import { makeApi } from 'core/api';
import { useDispatch } from 'store/hooks';
import { authFetch } from 'core/auth/actions';

export interface LoginHooks {
  top?: ReactNode;
  bottom?: ReactNode;
}

interface LoginPageProps {
  initial?: {
    email?: string;
  };
}

interface LoginCardProps {
  initial?: {
    email?: string;
  };
  hooks?: LoginHooks;
  onRegisterClick?: () => void;
  redirectToDashboard?: boolean;
}

export const LoginCard: FunctionComponent<LoginCardProps> = (props) => {
  const { hooks, initial, redirectToDashboard, onRegisterClick } = props;

  const { loading, error, login } = useLogin();
  const dispatch = useDispatch();

  const [email, setEmail] = useState(initial?.email || '');
  const [organizationName, setOrganizationName] = useState<string | null>(null);
  const [shouldUseGoogleSSO, setShouldUseGoogleSSO] = useState(false);
  const [ssoError, setSsoError] = useState<string | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const lookupOrganization = useCallback(async (userEmail: string) => {
    if (!userEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userEmail)) {
      setShouldUseGoogleSSO(false);
      setOrganizationName(null);
      return;
    }
    try {
      const api = makeApi(OrganizationsApi);
      const data = await api.organizationsRetrieve({
        userEmail: userEmail,
        platform: applicationConfig.platform,
      });
      if (data.loginType === LoginTypeEnum.GOOGLE_SSO) {
        setShouldUseGoogleSSO(true);
        setOrganizationName(data.name);
      } else {
        setShouldUseGoogleSSO(false);
        setOrganizationName(null);
      }
    } catch {
      setShouldUseGoogleSSO(false);
      setOrganizationName(null);
    }
  }, []);

  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      lookupOrganization(email);
    }, 400);
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [email, lookupOrganization]);

  // Handle SSO callback - backend redirects here with ?sso=success or ?sso=error
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const ssoResult = urlParams.get('sso');

    if (ssoResult === 'success') {
      // Session cookie is already set by the backend — just fetch the current user
      dispatch(authFetch());
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (ssoResult === 'error') {
      const detail = urlParams.get('detail') || 'Google authentication failed';
      setSsoError(detail);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [dispatch]);

  const handleGoogleLogin = () => {
    const params = new URLSearchParams({
      email,
      platform: applicationConfig.platform,
    });
    // Redirect to backend initiate endpoint — backend handles everything
    window.location.href = `/api/users/login/google/initiate/?${params.toString()}`;
  };

  const goToForgotPassword = useGoTo(AUTH_ROUTES.forgotPassword);

  const goToDashboard = useGoTo({ path: '/dashboard' });

  const handleLogin = (values: LoginFormFields) => {
    login(values.email, values.password, applicationConfig.platform);
    if (redirectToDashboard) {
      goToDashboard();
    }
  };

  return (
    <BoxedContent title={!hooks?.top && <Translate name="login.title" />}>
      <LoginForm
        error={error}
        onSubmit={handleLogin}
        onChange={(values) => {
          if (values.email !== email) {
            setEmail(values.email || '');
          }
        }}
        i18nKey="loginForm"
      >
        <LoginMask
          onForgotPassword={() => {
            goToForgotPassword();
          }}
          hooks={hooks}
          initial={initial}
          showPasswordField={!shouldUseGoogleSSO}
        />
        <LoginForm.GenericErrorMessages />
        {ssoError && (
          <p style={{ color: '#d32f2f', fontSize: '14px', textAlign: 'center', margin: '10px 0' }}>{ssoError}</p>
        )}
        <ActionButtons fullWidth spacingTop="small">
          {onRegisterClick && !shouldUseGoogleSSO && (
            <Button type="submit" variant="link" size="large" fluid onClick={onRegisterClick}>
              <Translate name="common.register" />
            </Button>
          )}

          {!shouldUseGoogleSSO && (
            <Button type="submit" variant="primary" size="large" loading={loading} fluid>
              <Translate name="common.login" />
            </Button>
          )}

          {shouldUseGoogleSSO && (
            <>
              <div
                className="google-signin-container"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  width: '100%',
                }}
              >
                <Button
                  type="button"
                  variant="primary"
                  size="large"
                  fluid
                  onClick={handleGoogleLogin}
                  loading={loading}
                >
                  <Translate name="login.signInWithGoogle" />
                </Button>
                <p
                  style={{
                    margin: '10px 0 0 0',
                    color: '#757575',
                    fontSize: '14px',
                    textAlign: 'center',
                  }}
                >
                  <Translate name="login.googleSsoRequired" args={[organizationName || '']} />
                </p>
              </div>
            </>
          )}
        </ActionButtons>
      </LoginForm>
    </BoxedContent>
  );
};

const LoginPage: FunctionComponent<LoginPageProps> = (props) => {
  const { initial } = props;

  const { origin: appType } = useRedirectPathSelector();

  const hooks =
    appType !== AppType.DEFAULT
      ? {
          top: (
            <Section>
              <Header>
                <Translate name={`login.header.${appType}`} />
              </Header>
              <Translate as="p" name="login.description" />
            </Section>
          ),
        }
      : {};

  return (
    <PublicPage>
      <NarrowContent>
        <LoginCard initial={initial} hooks={hooks} />
      </NarrowContent>
    </PublicPage>
  );
};

export default LoginPage;
